import { useState, useEffect, useRef } from 'react';
import { utils, writeFileXLSX } from 'xlsx';
import * as XLSX from 'xlsx';

// Define the backend URL as specified in your file.
// This is a hardcoded value as `process.env` is not available.
const BACKEND_URL = `${process.env.REACT_APP_BACKEND_URL}`;

/**
 * Custom React hook for managing the state and logic of the Digital Poster Generator application.
 * This hook handles API calls using native fetch, state management, and file operations.
 * It now includes logic to persist state to and from sessionStorage and uses custom modals.
 */
export const useImageGen = () => {
    // ------------------------------------------------------------------------
    // State Management
    // ------------------------------------------------------------------------

    // Helper function to get initial state from sessionStorage
    const getSessionStorageItem = (key, defaultValue) => {
        try {
            const storedValue = sessionStorage.getItem(key);
            if (storedValue) {
                return JSON.parse(storedValue);
            }
        } catch (error) {
            console.error(`Error parsing sessionStorage key '${key}':`, error);
            sessionStorage.removeItem(key);
        }
        return defaultValue;
    };

    // User-facing states and their initialization from sessionStorage
    const [manualPrompt, setManualPrompt] = useState(() => getSessionStorageItem('manualPrompt', ''));
    const [selectedClientId, setSelectedClientId] = useState(() => getSessionStorageItem('selectedClientId', ''));
    const [selectedSizePreset, setSelectedSizePreset] = useState(() => getSessionStorageItem('selectedSizePreset', 'default'));
    const [outputFormat, setOutputFormat] = useState(() => getSessionStorageItem('outputFormat', 'webp'));
    const [bulkSelectedSizePreset, setBulkSelectedSizePreset] = useState(() => getSessionStorageItem('bulkSelectedSizePreset', 'default'));
    const [bulkOutputFormat, setBulkOutputFormat] = useState(() => getSessionStorageItem('bulkOutputFormat', 'webp'));
    const [displayMode, setDisplayMode] = useState(() => getSessionStorageItem('displayMode', 'poster'));

    // UI and application logic states
    const [imageUrl, setImageUrl] = useState(() => getSessionStorageItem('lastGeneratedImageUrl', ''));
    const [displayedPrompt, setDisplayedPrompt] = useState(() => getSessionStorageItem('lastGeneratedPrompt', ''));
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [clients, setClients] = useState([]);
    const [generatedImageRecords, setGeneratedImageRecords] = useState([]);

    // Bulk generation states
    const [excelFile, setExcelFile] = useState(null);
    const [excelLoading, setExcelLoading] = useState(false);
    const [excelError, setExcelError] = useState('');
    const [excelResults, setExcelResults] = useState(() => getSessionStorageItem('excelResults', []));

    // Logo management states
    const [logoFile, setLogoFile] = useState(null);
    const [loadingLogoUpload, setLoadingLogoUpload] = useState(false);
    const [logoCrudError, setLogoCrudError] = useState('');
    const [allLogos, setAllLogos] = useState([]);
    const [selectedLogoId, setSelectedLogoId] = useState('');
    const [selectedLogoUrl, setSelectedLogoUrl] = useState('');

    // Logo combination states
    const [logoCombineExcelFile, setLogoCombineExcelFile] = useState(null);
    const [logoCombineLoading, setLogoCombineLoading] = useState(false);
    const [logoCombineError, setLogoCombineError] = useState('');
    const [logoCombineResults, setLogoCombineResults] = useState([]);
    const [originalCombineExcelData, setOriginalCombineExcelData] = useState([]);
    const [outputFormatForCombiner, setOutputFormatForCombiner] = useState('webp');

    // Custom Modal states
    const [showModal, setShowModal] = useState(false);
    const [modalMessage, setModalMessage] = useState('');
    const [modalCallback, setModalCallback] = useState(null);
    const [modalIsConfirm, setModalIsConfirm] = useState(false);

    // Ref to manage file input for reset
    const fileInputRef = useRef(null);
    const logoFileInputRef = useRef(null);
    const logoCombineFileInputRef = useRef(null);

    // Image size and format options
    const availableSizePresets = [
        { value: 'default', label: '1024x1024 (Default)' },
        { value: '1080p', label: '1920x1080 (1080p)' },
        { value: '720p', label: '1280x720 (720p)' },
        { value: 'instagram_post', label: '1080x1080 (Instagram Post)' },
        { value: 'instagram_portrait', label: '1080x1350 (Instagram Portrait)' },
        { value: 'instagram_landscape', label: '1080x566 (Instagram Landscape)' },
        { value: 'facebook_post_square', label: '1200x1200 (Facebook Square Post)' },
        { value: 'facebook_post_landscape', label: '1200x630 (Facebook Landscape Post)' },
        { value: 'blog_post_featured', width: 1200, height: 630, label: '1200x630 (Blog Post Featured)' },
        { value: '400x300', label: '400x300' }
    ];
    const availableFormats = [
        { value: 'webp', label: 'WEBP (Recommended)' },
        { value: 'jpeg', label: 'JPEG' },
        { value: 'png', label: 'PNG' },
    ];

    // ------------------------------------------------------------------------
    // Custom Modal Functions
    // ------------------------------------------------------------------------
    const showMessage = (message) => {
        setModalMessage(message);
        setModalIsConfirm(false);
        setModalCallback(null);
        setShowModal(true);
    };

    const showConfirm = (message, onConfirm) => {
        setModalMessage(message);
        setModalIsConfirm(true);
        setModalCallback(() => onConfirm);
        setShowModal(true);
    };

    const handleModalClose = (confirmed) => {
        setShowModal(false);
        if (modalIsConfirm && modalCallback) {
            modalCallback(confirmed);
        }
    };

    // ------------------------------------------------------------------------
    // useEffect hooks for sessionStorage
    // ------------------------------------------------------------------------

    // Save states to sessionStorage whenever they change
    useEffect(() => {
        sessionStorage.setItem('manualPrompt', JSON.stringify(manualPrompt));
    }, [manualPrompt]);

    useEffect(() => {
        sessionStorage.setItem('selectedClientId', JSON.stringify(selectedClientId));
    }, [selectedClientId]);

    useEffect(() => {
        sessionStorage.setItem('selectedSizePreset', JSON.stringify(selectedSizePreset));
    }, [selectedSizePreset]);

    useEffect(() => {
        sessionStorage.setItem('outputFormat', JSON.stringify(outputFormat));
    }, [outputFormat]);

    useEffect(() => {
        sessionStorage.setItem('bulkSelectedSizePreset', JSON.stringify(bulkSelectedSizePreset));
    }, [bulkSelectedSizePreset]);

    useEffect(() => {
        sessionStorage.setItem('bulkOutputFormat', JSON.stringify(bulkOutputFormat));
    }, [bulkOutputFormat]);

    useEffect(() => {
        sessionStorage.setItem('displayMode', JSON.stringify(displayMode));
    }, [displayMode]);

    useEffect(() => {
        sessionStorage.setItem('lastGeneratedImageUrl', JSON.stringify(imageUrl));
    }, [imageUrl]);

    useEffect(() => {
        sessionStorage.setItem('lastGeneratedPrompt', JSON.stringify(displayedPrompt));
    }, [displayedPrompt]);
    
    // NEW: Persist excelResults to sessionStorage
    useEffect(() => {
        sessionStorage.setItem('excelResults', JSON.stringify(excelResults));
    }, [excelResults]);

    // ------------------------------------------------------------------------
    // API Call & Data Fetching
    // ------------------------------------------------------------------------

    // Fetch clients on component mount
    useEffect(() => {
        const fetchClients = async () => {
            const token = localStorage.getItem("token");
            try {
                if (!BACKEND_URL) {
                    setError("Backend URL is not configured.");
                    console.error("Backend URL is not defined.");
                    return;
                }
                const response = await fetch(`${BACKEND_URL}/api/client`, {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                });
                const data = await response.json();
                if (!response.ok) {
                    throw new Error(data.message || 'Failed to fetch clients');
                }
                setClients(data);
            } catch (err) {
                console.error('Error fetching clients:', err.message);
                setError('Failed to load clients. Please check your backend server and authentication.');
            }
        };
        fetchClients();
    }, []);

    // Fetch logos on component mount
    const fetchAllLogos = async () => {
        setLogoCrudError('');
        const token = localStorage.getItem("token");
        try {
            if (!BACKEND_URL) {
                setLogoCrudError("Backend URL is not configured for logo fetching.");
                return;
            }
            const response = await fetch(`${BACKEND_URL}/api/logos`, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.message || 'Failed to fetch logos');
            }
            setAllLogos(data);
            if (selectedLogoId && !data.some(logo => logo._id === selectedLogoId)) {
                setSelectedLogoId('');
                setSelectedLogoUrl('');
            } else if (selectedLogoId) {
                const currentSelected = data.find(logo => logo._id === selectedLogoId);
                if (currentSelected) {
                    setSelectedLogoUrl(`${BACKEND_URL}${currentSelected.path}`);
                }
            }
        } catch (err) {
            console.error('Error fetching all logos:', err.message);
            setLogoCrudError('Failed to load logos.');
            setAllLogos([]);
            setSelectedLogoId('');
            setSelectedLogoUrl('');
        }
    };

    useEffect(() => {
        fetchAllLogos();
    }, []);

    // ------------------------------------------------------------------------
    // Event Handlers
    // ------------------------------------------------------------------------

    // General function to clear states for a specific generation type
    const clearSingleGenerationStates = () => {
        setManualPrompt('');
        setSelectedClientId('');
        setImageUrl('');
        setDisplayedPrompt('');
        setGeneratedImageRecords([]);
        setError('');
        sessionStorage.removeItem('lastGeneratedImageUrl');
        sessionStorage.removeItem('lastGeneratedPrompt');
    };

    const clearBulkGenerationStates = () => {
        setExcelFile(null);
        setExcelResults([]);
        setExcelError('');
        setBulkSelectedSizePreset('default');
        setBulkOutputFormat('webp');
        if (fileInputRef.current) fileInputRef.current.value = '';
        sessionStorage.removeItem('excelResults');
    };

    const clearLogoCombinerStates = () => {
        setLogoCombineExcelFile(null);
        setLogoCombineResults([]);
        setOriginalCombineExcelData([]);
        setLogoCombineError('');
        if (logoCombineFileInputRef.current) logoCombineFileInputRef.current.value = '';
    };


    // Handlers for Image Generation Inputs (independent of Logo CRUD)
    const handleClientSelectChange = (e) => {
        clearBulkGenerationStates();
        clearLogoCombinerStates();
        setSelectedClientId(e.target.value);
    };

    const handleManualPromptChange = (e) => {
        clearBulkGenerationStates();
        clearLogoCombinerStates();
        setManualPrompt(e.target.value);
    };

    const handleExcelFileChange = (e) => {
        clearSingleGenerationStates();
        clearLogoCombinerStates();
        setExcelFile(e.target.files[0]);
    };

    const handleSizePresetChange = (e) => {
        setSelectedSizePreset(e.target.value);
    };

    const handleFormatChange = (e) => {
        setOutputFormat(e.target.value);
    };

    const handleBulkSizePresetChange = (e) => {
        setBulkSelectedSizePreset(e.target.value);
    };

    const handleBulkOutputFormatChange = (e) => {
        setBulkOutputFormat(e.target.value);
    };


    // Handlers for Logo CRUD Inputs (independent of Image Generation)
    const handleLogoFileChange = (e) => {
        setLogoFile(e.target.files[0]);
    };

    const handleLogoSelectChange = (e) => {
        const id = e.target.value;
        setSelectedLogoId(id);
        const selected = allLogos.find(logo => logo._id === id);
        if (selected) {
            setSelectedLogoUrl(`${BACKEND_URL}${selected.path}`);
        } else {
            setSelectedLogoUrl('');
        }
    };

    const handleLogoCombineExcelChange = (e) => {
        clearSingleGenerationStates();
        clearBulkGenerationStates();
        const file = e.target.files[0];
        if (file && (file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || file.type === 'application/vnd.ms-excel')) {
            setLogoCombineExcelFile(file);
            setLogoCombineError('');
            const reader = new FileReader();
            reader.onload = (event) => {
                try {
                    const data = new Uint8Array(event.target.result);
                    const workbook = XLSX.read(data, { type: 'array' });
                    const sheetName = workbook.SheetNames[0];
                    const worksheet = workbook.Sheets[sheetName];
                    const json = XLSX.utils.sheet_to_json(worksheet);
                    setOriginalCombineExcelData(json);
                } catch (error) {
                    setLogoCombineError("Error reading Excel file for combiner. Make sure it's a valid Excel format.");
                    setOriginalCombineExcelData([]);
                }
            };
            reader.onerror = (error) => {
                setLogoCombineError("File reading failed for combiner.");
                setOriginalCombineExcelData([]);
            };
            reader.readAsArrayBuffer(file);
        } else {
            setLogoCombineExcelFile(null);
            setLogoCombineError('Please select a valid Excel file (.xlsx or .xls).');
            setOriginalCombineExcelData([]);
        }
    };

    const handleOutputFormatForCombinerChange = (e) => {
        setOutputFormatForCombiner(e.target.value);
    };

    // --- Handle single image generation (via Client ID or Manual Prompt) ---
    const handleSingleGenerate = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setImageUrl('');
        setDisplayedPrompt('');
        setGeneratedImageRecords([]);

        clearBulkGenerationStates();
        clearLogoCombinerStates();

        if (!BACKEND_URL) {
            setError("Backend URL is not configured.");
            setLoading(false);
            return;
        }

        let payload = {
            sizePreset: selectedSizePreset,
            outputFormat: outputFormat,
        };

        if (selectedClientId) {
            payload.clientId = selectedClientId;
        } else if (manualPrompt.trim()) {
            payload.prompt = manualPrompt.trim();
        } else {
            setError('Please select a client or enter a manual prompt to generate an image.');
            setLoading(false);
            return;
        }

        const token = localStorage.getItem("token");

        try {
            const response = await fetch(`${BACKEND_URL}/api/ImageGen`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload),
            });

            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.message || 'Failed to generate image.');
            }

            // Corrected: Add a timestamp to the image URL to prevent browser caching
            const timestamp = new Date().getTime();
            const absoluteImageUrl = `${BACKEND_URL}${data.imageUrl}?v=${timestamp}`;
            setImageUrl(absoluteImageUrl);
            setDisplayedPrompt(data.prompt);

            // Add the single generated image and client data to records for Excel download
            const filename = data.imageUrl.split('/').pop();
            const clientSocialMedia = {};
            if (data.clientData && data.clientData.socialMediaAccounts) {
                data.clientData.socialMediaAccounts.forEach((account, index) => {
                    if (index < 3) {
                        clientSocialMedia[`Social_Platform_${index + 1}`] = account.platform;
                        clientSocialMedia[`Social_Username_${index + 1}`] = account.username;
                        clientSocialMedia[`Social_Password_${index + 1}`] = account.password;
                    }
                });
            }

            setGeneratedImageRecords([{
                prompt: data.prompt,
                filename: filename,
                clientName: data.clientData ? data.clientData.clientName : 'N/A',
                ...clientSocialMedia
            }]);

        } catch (err) {
            console.error('Frontend error during single image generation:', err.message);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // --- Handle Excel file upload and bulk generation (AI) ---
    const handleExcelUpload = async () => {
        setExcelLoading(true);
        setExcelError('');
        setExcelResults([]);
        clearSingleGenerationStates();
        clearLogoCombinerStates();

        if (!excelFile) {
            setExcelError('Please select an Excel file to upload.');
            setExcelLoading(false);
            return;
        }

        const formData = new FormData();
        formData.append('excelFile', excelFile);
        formData.append('defaultSizePreset', bulkSelectedSizePreset);
        formData.append('defaultOutputFormat', bulkOutputFormat);

        const token = localStorage.getItem("token");

        try {
            const response = await fetch(`${BACKEND_URL}/api/ImageGen/bulk`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
                body: formData,
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.message || 'Failed to process Excel file and generate images.');
            }
            setExcelResults(data.results);
            showMessage(`Bulk image generation complete! Generated: ${data.results.filter(r => r.success).length} images. Failed: ${data.results.filter(r => !r.success).length} images.`);
        } catch (err) {
            console.error('Frontend error during bulk image generation:', err.message);
            setExcelError(err.message);
        } finally {
            setExcelLoading(false);
            setExcelFile(null);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    // --- NEW: Handle Logo File Upload ---
    const handleUploadLogo = async () => {
        setLoadingLogoUpload(true);
        setLogoCrudError('');

        if (!logoFile) {
            setLogoCrudError('Please select a logo file to upload.');
            setLoadingLogoUpload(false);
            return;
        }

        const formData = new FormData();
        formData.append('logoFile', logoFile);

        const token = localStorage.getItem("token");

        try {
            const response = await fetch(`${BACKEND_URL}/api/logos`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
                body: formData,
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.message || 'Failed to upload logo.');
            }
            showMessage('Logo uploaded successfully!');
            setLogoFile(null);
            if (logoFileInputRef.current) logoFileInputRef.current.value = '';
            await fetchAllLogos();
        } catch (err) {
            console.error('Frontend error during logo upload:', err.message);
            setLogoCrudError(err.message);
        } finally {
            setLoadingLogoUpload(false);
        }
    };

    // --- NEW: Handle Logo Deletion ---
    const handleDeleteLogo = async () => {
        if (!selectedLogoId) {
            setLogoCrudError('Please select a logo to delete.');
            return;
        }

        showConfirm('Are you sure you want to delete this logo? This action cannot be undone.', async (confirmed) => {
            if (confirmed) {
                setLoadingLogoUpload(true);
                setLogoCrudError('');
                const token = localStorage.getItem("token");

                try {
                    const response = await fetch(`${BACKEND_URL}/api/logos/${selectedLogoId}`, {
                        method: 'DELETE',
                        headers: {
                            'Authorization': `Bearer ${token}`,
                        },
                    });
                    const data = await response.json();
                    if (!response.ok) {
                        throw new Error(data.message || 'Failed to delete logo.');
                    }
                    showMessage('Logo deleted successfully!');
                    setSelectedLogoId('');
                    setSelectedLogoUrl('');
                    await fetchAllLogos();
                } catch (err) {
                    console.error('Frontend error during logo deletion:', err.message);
                    setLogoCrudError(err.message);
                } finally {
                    setLoadingLogoUpload(false);
                }
            }
        });
    };

    // --- Function to handle single image download (for non-Excel generation) ---
    const handleSingleDownload = async () => {
        if (!imageUrl) {
            setError("No image to download.");
            return;
        }
        try {
            const response = await fetch(imageUrl);
            const blob = await response.blob();
            const clientNameForFilename = clients.find(c => c._id === selectedClientId)?.clientName.replace(/\s+/g, '-') || 'custom-image';
            const fileExtension = imageUrl.split('.').pop().split('?')[0] || outputFormat;
            const filename = `${clientNameForFilename}-${Date.now()}.${fileExtension}`;
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(link.href);
        } catch (downloadError) {
            console.error('Error during single image download:', downloadError);
            setError('Failed to download image. Please try again.');
        }
    };

    // --- Function to handle batch download for Excel generated images ---
    const handleBatchDownload = async () => {
        if (excelResults.length === 0) {
            setExcelError("No images to download from Excel batch.");
            return;
        }
        setExcelLoading(true);
        setExcelError('');
        for (const imgData of excelResults) {
            if (imgData.success && imgData.imageUrl) {
                try {
                    const response = await fetch(imgData.imageUrl);
                    const blob = await response.blob();
                    const filename = imgData.filename;
                    const link = document.createElement('a');
                    link.href = URL.createObjectURL(blob);
                    link.download = filename;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    URL.revokeObjectURL(link.href);
                } catch (downloadError) {
                    console.error(`Error downloading image for prompt "${imgData.prompt}":`, downloadError);
                }
            }
        }
        setExcelLoading(false);
        showMessage("Batch download initiated. Check your downloads folder. Some images might have failed.");
    };

    // --- NEW: Function to download generated images data as Excel (Single AI Generation) ---
    const handleDownloadGeneratedExcel = () => {
        if (generatedImageRecords.length === 0) {
            showMessage("No generated image data to download. Please generate a single image first.");
            return;
        }
        const ws = utils.json_to_sheet(generatedImageRecords);
        const wb = utils.book_new();
        utils.book_append_sheet(wb, ws, "GeneratedImageReport");
        const excelFileName = `Generated_Image_Report_${Date.now()}.xlsx`;
        writeFileXLSX(wb, excelFileName);
        showMessage(`Excel file "${excelFileName}" downloaded successfully!`);
    };

    // --- NEW: Function to handle processing images with selected logo from Excel ---
    const handleProcessImagesWithSelectedLogo = async () => {
        setLogoCombineLoading(true);
        setLogoCombineError('');
        setLogoCombineResults([]);

        clearSingleGenerationStates();
        clearBulkGenerationStates();

        if (!logoCombineExcelFile) {
            setLogoCombineError('Please select an Excel file for logo combining.');
            setLogoCombineLoading(false);
            return;
        }
        if (!selectedLogoId) {
            setLogoCombineError('Please select an existing logo from the "Select Existing Logo" dropdown.');
            setLogoCombineLoading(false);
            return;
        }
        if (originalCombineExcelData.length === 0) {
            setLogoCombineError('Original Excel data not loaded. Please re-select the Excel file.');
            setLogoCombineLoading(false);
            return;
        }

        const formData = new FormData();
        formData.append('excelFile', logoCombineExcelFile);
        formData.append('selectedLogoId', selectedLogoId);
        formData.append('outputFormat', outputFormatForCombiner);

        const token = localStorage.getItem("token");

        try {
            const response = await fetch(`${BACKEND_URL}/api/process-images/process-excel`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
                body: formData,
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.message || 'Failed to process images with logo.');
            }
            setLogoCombineResults(data.results);

            if (data.results.some(r => !r.success)) {
                setLogoCombineError('Some images failed to process. Check results below and download the updated Excel.');
            } else {
                showMessage('All images processed successfully with logo overlay! Download the updated Excel report.');
            }
        } catch (err) {
            console.error('Frontend error during logo combining:', err.message);
            setLogoCombineError(err.message);
        } finally {
            setLogoCombineLoading(false);
        }
    };

    // --- NEW: Function to download the logo combined image report as Excel ---
    const handleDownloadLogoCombineExcel = () => {
        if (logoCombineResults.length === 0) {
            showMessage("No logo combined image data to download. Please process images first.");
            return;
        }
        if (originalCombineExcelData.length === 0) {
            showMessage("Original Excel data is missing. Please re-upload the Excel file and process images.");
            return;
        }

        const processedMap = new Map();
        logoCombineResults.forEach(result => {
            if (result.originalFilename) {
                processedMap.set(result.originalFilename, result.processedFilename || 'Failed');
            }
        });

        const dataToExport = originalCombineExcelData.map(originalRow => {
            const originalFilename = originalRow.filename;
            const processedFilename = processedMap.get(originalFilename) || '';

            return {
                ...originalRow,
                'Original_Image_Filename': originalFilename,
                'Processed_Image_Filename': processedFilename
            };
        });

        const ws = utils.json_to_sheet(dataToExport);
        const wb = utils.book_new();
        utils.book_append_sheet(wb, ws, "Combined_Image_Report");

        const excelFileName = `Combined_Image_Report_${Date.now()}.xlsx`;
        writeFileXLSX(wb, excelFileName);
        showMessage(`Excel file "${excelFileName}" downloaded successfully!`);

        setLogoCombineExcelFile(null);
        setLogoCombineResults([]);
        setOriginalCombineExcelData([]);
        if (logoCombineFileInputRef.current) logoCombineFileInputRef.current.value = '';
    };

    // --- Regenerate a bulk image ---
    const handleBulkRegenerate = async (result, index) => {
        setExcelLoading(true);
        setExcelError('');
        try {
            const payload = {
                prompt: result.prompt,
                clientId: result.clientId || '',
                sizePreset: bulkSelectedSizePreset,
                outputFormat: bulkOutputFormat,
                topic: result.topic || '',
                forceFilename: result.filename,
            };
            const token = localStorage.getItem("token");

            const response = await fetch(`${BACKEND_URL}/api/ImageGen`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload),
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.message || 'Failed to regenerate image.');
            }
            const updatedResults = [...excelResults];
            updatedResults[index] = {
                ...updatedResults[index],
                success: true,
                imageUrl: data.imageUrl,
                filename: result.filename,
                error: '',
            };
            setExcelResults(updatedResults);
            showMessage('Image regenerated successfully!');
        } catch (err) {
            const errorMessage = err.message;
            const updatedResults = [...excelResults];
            updatedResults[index] = {
                ...updatedResults[index],
                success: false,
                error: errorMessage,
            };
            setExcelResults(updatedResults);
            setExcelError(errorMessage);
        } finally {
            setExcelLoading(false);
        }
    };

    // --- Delete a bulk image ---
    const handleBulkDelete = async (result, index) => {
        showConfirm('Are you sure you want to delete this image? This action cannot be undone.', async (confirmed) => {
            if (confirmed) {
                setExcelLoading(true);
                setExcelError('');
                try {
                    const filename = result.filename;
                    if (!filename) throw new Error('No filename found for deletion.');
                    const token = localStorage.getItem("token");

                    const response = await fetch(`${BACKEND_URL}/api/imageGen/${filename}`, {
                        method: 'DELETE',
                        headers: {
                            'Authorization': `Bearer ${token}`
                        }
                    });
                    const data = await response.json();
                    if (!response.ok) {
                        throw new Error(data.message || 'Failed to delete image.');
                    }
                    const updatedResults = excelResults.filter((_, i) => i !== index);
                    setExcelResults(updatedResults);
                    showMessage('Image deleted successfully!');
                } catch (err) {
                    setExcelError(err.message);
                } finally {
                    setExcelLoading(false);
                }
            }
        });
    };

    // --- Determine if single generation inputs should be disabled
    const isSingleGenDisabled = excelLoading || logoCombineLoading || excelFile !== null || logoCombineExcelFile !== null;

    // --- Determine if bulk generation inputs should be disabled
    const isBulkGenDisabled = loading || logoCombineLoading || selectedClientId !== '' || manualPrompt.trim() !== '' || logoCombineExcelFile !== null;

    // --- Determine if logo combiner inputs should be disabled
    const isLogoCombinerDisabled = loading || excelLoading || selectedClientId !== '' || manualPrompt.trim() !== '' || excelFile !== null;

    return {
        // States
        manualPrompt, setManualPrompt,
        imageUrl, setImageUrl,
        loading, setLoading,
        error, setError,
        clients, setClients,
        selectedClientId, setSelectedClientId,
        displayedPrompt, setDisplayedPrompt,
        excelFile, setExcelFile,
        excelLoading, setExcelLoading,
        excelError, setExcelError,
        excelResults, setExcelResults,
        fileInputRef,
        bulkSelectedSizePreset, setBulkSelectedSizePreset,
        bulkOutputFormat, setBulkOutputFormat,
        logoFile, setLogoFile,
        allLogos, setAllLogos,
        selectedLogoId, setSelectedLogoId,
        selectedLogoUrl, setSelectedLogoUrl,
        logoFileInputRef,
        loadingLogoUpload, setLoadingLogoUpload,
        logoCrudError, setLogoCrudError,
        generatedImageRecords, setGeneratedImageRecords,
        logoCombineExcelFile, setLogoCombineExcelFile,
        logoCombineLoading, setLogoCombineLoading,
        logoCombineError, setLogoCombineError,
        logoCombineResults, setLogoCombineResults,
        originalCombineExcelData, setOriginalCombineExcelData,
        logoCombineFileInputRef,
        selectedSizePreset, setSelectedSizePreset,
        outputFormat, setOutputFormat,
        outputFormatForCombiner, setOutputFormatForCombiner,
        displayMode, setDisplayMode,
        availableSizePresets, availableFormats,

        // Handlers & Functions
        clearSingleGenerationStates,
        clearBulkGenerationStates,
        clearLogoCombinerStates,
        handleClientSelectChange,
        handleManualPromptChange,
        handleExcelFileChange,
        handleSizePresetChange,
        handleFormatChange,
        handleBulkSizePresetChange,
        handleBulkOutputFormatChange,
        handleLogoFileChange,
        handleLogoSelectChange,
        handleLogoCombineExcelChange,
        handleOutputFormatForCombinerChange,
        handleSingleGenerate,
        handleExcelUpload,
        handleUploadLogo,
        handleDeleteLogo,
        handleSingleDownload,
        handleBatchDownload,
        handleDownloadGeneratedExcel,
        handleProcessImagesWithSelectedLogo,
        handleDownloadLogoCombineExcel,
        handleBulkRegenerate,
        handleBulkDelete,
        isSingleGenDisabled,
        isBulkGenDisabled,
        isLogoCombinerDisabled,
        BACKEND_URL,

        // Custom Modal
        showModal,
        modalMessage,
        modalIsConfirm,
        handleModalClose
    };
};